import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  CampoTelefone,
  Cartao,
  Selo,
  useDialogo,
} from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { useDesativarEmpresa, useEditarEmpresa } from '../hooks/useEscrita';
import { formatarCnpj } from '../servicos/formato';
import { opcoesDeRegime } from '../servicos/empresas';
import { ErroDaApi } from './ErroDaApi';

interface EmpresaDetalhada {
  readonly id: string;
  readonly taxpayerId: string;
  readonly legalName: string;
  readonly name: string | null;
  readonly emailAddress: string;
  readonly phone: string | null;
  readonly addressCity: string | null;
  readonly addressStateCode: string | null;
  readonly addressStreetName: string | null;
  readonly addressStreetNumber: string | null;
  readonly addressDistrict: string | null;
  readonly addressPostalCode: string | null;
  readonly addressExtraInfo: string | null;
  readonly industryClassification: string | null;
  readonly taxRegime: string | null;
  readonly cadIcms: string | null;
  readonly credencialSiscred: string | null;
  /** Como a Receita informa; vazio até a primeira consulta. */
  readonly cnaes: readonly CnaeDaEmpresa[];
  /** `null` = a empresa nunca foi consultada na Receita. */
  readonly receitaConsultadaEm: string | null;
  readonly cadastradaEm: string;
  readonly isActive: boolean;
}

interface CnaeDaEmpresa {
  readonly codigo: string;
  readonly descricao: string;
  readonly principal: boolean;
}

function dataEHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * RN-305 — Editar o cadastro da empresa.
 *
 * ## O que faltava
 *
 * `PATCH /business/:id` e o hook `useEditarEmpresa` existiam, e **nenhuma tela
 * os chamava**: razão social, e-mail, telefone e endereço eram imutáveis pela
 * interface. Uma empresa que mudou de endereço só mudava por SQL.
 *
 * ## O CNPJ não está aqui
 *
 * De propósito, e a API concorda: `taxpayerId` fica de fora do schema de edição.
 * Ele é a identidade da empresa e a chave que amarra SPED, contrato e vínculos —
 * trocá-lo não é editar uma empresa, é apontar todo o histórico dela para outra
 * pessoa jurídica.
 *
 * ## Regime e CNAEs vêm da Receita
 *
 * Ninguém digita CNAE: a primeira abertura de uma empresa nunca consultada
 * busca na Receita sozinha (`POST /business/:id/receita`), e "Atualizar pela
 * Receita" busca de novo. O regime continua um select — a Receita conhece o
 * Simples na hora, mas o Lucro Real/Presumido só pela declaração do ano
 * anterior, e quem cuida da empresa corrige quando ela mudou de regime.
 *
 * ## Só o que mudou é enviado
 *
 * O `PATCH` é parcial e campo ausente é campo não alterado. Mandar o formulário
 * inteiro transformaria "não mexi no telefone" em "apague o telefone" — que é o
 * bug que o comentário de `opcionais()` na API descreve.
 */
export function CadastroDaEmpresa(props: {
  readonly businessId: string;
  /**
   * Para onde ir depois de desativar. A tela do cliente volta para a carteira
   * de clientes; a de empresas, para a listagem de empresas — em ambos os
   * casos, ficar na tela de uma empresa que acabou de sumir da listagem é o
   * caminho para um 404 logo em seguida.
   */
  readonly rotaAposDesativar?: string;
  /**
   * Quando o formulário é uma seção entre outras (dados bancários, documentos,
   * Drive), o cartão de desativar não pode ficar no meio da página: quem o
   * renderiza por último é a aba, com `DesativacaoDaEmpresa`.
   */
  readonly ocultarDesativacao?: boolean;
  /**
   * Quem só acompanha (o fornecedor do cliente, um colaborador) vê o cadastro,
   * sem editar: os campos ficam travados e o "Salvar" some.
   */
  readonly somenteLeitura?: boolean;
}): JSX.Element {
  const leitura = props.somenteLeitura === true;
  const empresa = useQuery({
    queryKey: ['empresa', props.businessId],
    queryFn: () => requisicaoAutenticada<EmpresaDetalhada>(`/business/${props.businessId}`),
  });

  const editar = useEditarEmpresa(props.businessId);
  const consultas = useQueryClient();

  const [form, setForm] = useState<Record<string, string> | null>(null);

  const receita = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<EmpresaDetalhada>(`/business/${props.businessId}/receita`, {
        metodo: 'POST',
      }),
    onSuccess: (atualizada) => {
      consultas.setQueryData(['empresa', props.businessId], atualizada);
      // O regime passa a ser o da Receita; o resto do que foi digitado fica.
      setForm((f) => (f === null ? f : { ...f, taxRegime: atualizada.taxRegime ?? '' }));
    },
  });
  const { mutate: consultarReceita } = receita;

  // Empresa nunca consultada: a primeira abertura por quem cuida dela busca
  // sozinha, uma vez. Se a Receita falhar, a próxima abertura tenta de novo.
  const buscouSozinha = useRef(false);
  useEffect(() => {
    if (leitura || buscouSozinha.current) return;
    if (empresa.data === undefined || empresa.data.receitaConsultadaEm !== null) return;
    buscouSozinha.current = true;
    consultarReceita();
  }, [empresa.data, leitura, consultarReceita]);

  // O formulário nasce do que está gravado, e só quando a resposta chega.
  useEffect(() => {
    const d = empresa.data;
    if (d === undefined || form !== null) return;
    setForm({
      legalName: d.legalName,
      name: d.name ?? '',
      emailAddress: d.emailAddress,
      phone: d.phone ?? '',
      addressPostalCode: d.addressPostalCode ?? '',
      addressStreetName: d.addressStreetName ?? '',
      addressStreetNumber: d.addressStreetNumber ?? '',
      addressExtraInfo: d.addressExtraInfo ?? '',
      addressDistrict: d.addressDistrict ?? '',
      addressCity: d.addressCity ?? '',
      addressStateCode: d.addressStateCode ?? '',
      taxRegime: d.taxRegime ?? '',
      cadIcms: d.cadIcms ?? '',
      credencialSiscred: d.credencialSiscred ?? '',
    });
  }, [empresa.data, form]);

  if (empresa.isError) {
    return <ErroDaApi titulo="Não foi possível carregar o cadastro" erro={empresa.error} />;
  }
  if (empresa.data === undefined || form === null) {
    return <p className="py-8 text-center text-sm text-conteudo-sutil">Carregando cadastro…</p>;
  }

  const dados = empresa.data;
  const definir = (campo: string, valor: string): void =>
    setForm((f) => (f === null ? f : { ...f, [campo]: valor }));

  /** Só os campos que mudaram — ver a nota da classe. */
  const alteracoes = (): Record<string, string> => {
    const original: Record<string, string> = {
      legalName: dados.legalName,
      name: dados.name ?? '',
      emailAddress: dados.emailAddress,
      phone: dados.phone ?? '',
      addressPostalCode: dados.addressPostalCode ?? '',
      addressStreetName: dados.addressStreetName ?? '',
      addressStreetNumber: dados.addressStreetNumber ?? '',
      addressExtraInfo: dados.addressExtraInfo ?? '',
      addressDistrict: dados.addressDistrict ?? '',
      addressCity: dados.addressCity ?? '',
      addressStateCode: dados.addressStateCode ?? '',
      taxRegime: dados.taxRegime ?? '',
      cadIcms: dados.cadIcms ?? '',
      credencialSiscred: dados.credencialSiscred ?? '',
    };

    return Object.fromEntries(
      Object.entries(form).filter(([chave, valor]) => valor !== original[chave]),
    );
  };

  const mudou = Object.keys(alteracoes()).length > 0;

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!leitura) editar.mutate(alteracoes());
        }}
      >
        {/* `disabled` no fieldset trava todos os campos de uma vez, sem um por um. */}
        <fieldset disabled={leitura} className="flex min-w-0 flex-col gap-4">
          <Cartao como="section" titulo="Identificação">
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wide text-conteudo-sutil">CNPJ</span>
                  <span className="valor-fiscal text-sm text-conteudo-primario">
                    {formatarCnpj(dados.taxpayerId)}
                  </span>
                  <span className="text-xs text-conteudo-sutil">
                    Não editável: é a chave que amarra SPED, contrato e vínculos.
                  </span>
                  <span className="mt-2 text-xs text-conteudo-sutil">
                    Cadastrada em{' '}
                    {new Date(dados.cadastradaEm).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                  </span>
                </div>
                <Campo
                  rotulo="Nome fantasia"
                  value={form['name'] ?? ''}
                  onChange={(e) => definir('name', e.target.value)}
                />
              </div>
              <Campo
                rotulo="Razão social"
                required
                value={form['legalName'] ?? ''}
                onChange={(e) => definir('legalName', e.target.value)}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  rotulo="E-mail"
                  type="email"
                  required
                  value={form['emailAddress'] ?? ''}
                  onChange={(e) => definir('emailAddress', e.target.value)}
                />
                <CampoTelefone
                  rotulo="Telefone"
                  value={form['phone'] ?? ''}
                  onChange={(valor) => definir('phone', valor)}
                />
              </div>
            </div>
          </Cartao>

          <Cartao como="section" titulo="Endereço">
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-[1fr_8rem_10rem]">
                <Campo
                  rotulo="Logradouro"
                  value={form['addressStreetName'] ?? ''}
                  onChange={(e) => definir('addressStreetName', e.target.value)}
                />
                <Campo
                  rotulo="Número"
                  value={form['addressStreetNumber'] ?? ''}
                  onChange={(e) => definir('addressStreetNumber', e.target.value)}
                />
                <Campo
                  rotulo="Complemento"
                  value={form['addressExtraInfo'] ?? ''}
                  onChange={(e) => definir('addressExtraInfo', e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-[1fr_1fr_10rem_6rem]">
                <Campo
                  rotulo="Bairro"
                  value={form['addressDistrict'] ?? ''}
                  onChange={(e) => definir('addressDistrict', e.target.value)}
                />
                <Campo
                  rotulo="Cidade"
                  value={form['addressCity'] ?? ''}
                  onChange={(e) => definir('addressCity', e.target.value)}
                />
                <Campo
                  rotulo="CEP"
                  inputMode="numeric"
                  value={form['addressPostalCode'] ?? ''}
                  onChange={(e) => definir('addressPostalCode', e.target.value)}
                />
                <Campo
                  rotulo="UF"
                  maxLength={2}
                  value={form['addressStateCode'] ?? ''}
                  onChange={(e) => definir('addressStateCode', e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </Cartao>

          <Cartao
            como="section"
            titulo="Dados fiscais"
            subtitulo={
              dados.receitaConsultadaEm === null
                ? 'Regime tributário e CNAEs vêm da Receita Federal.'
                : `Conforme a Receita Federal, consultada em ${dataEHora(dados.receitaConsultadaEm)}.`
            }
            {...(leitura
              ? {}
              : {
                  acao: (
                    <Botao
                      type="button"
                      variante="secundario"
                      tamanho="pequeno"
                      carregando={receita.isPending}
                      onClick={() => receita.mutate()}
                    >
                      Atualizar pela Receita
                    </Botao>
                  ),
                })}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {receita.isError && (
                <div className="sm:col-span-2">
                  <ErroDaApi
                    titulo="A Receita não respondeu agora — os dados abaixo são os gravados"
                    erro={receita.error}
                  />
                </div>
              )}
              <CampoDeSelecao
                rotulo="Regime tributário"
                ajuda="Vem da Receita. Ajuste só se a empresa mudou de regime este ano."
                value={form['taxRegime'] ?? ''}
                onChange={(e) => definir('taxRegime', e.target.value)}
                opcoes={opcoesDeRegime(form['taxRegime'] ?? '')}
              />
              <Campo
                rotulo="CAD/ICMS"
                ajuda="Inscrição estadual usada no contrato e no SPED."
                value={form['cadIcms'] ?? ''}
                onChange={(e) => definir('cadIcms', e.target.value)}
              />
              <Campo
                rotulo="Credencial SISCRED"
                value={form['credencialSiscred'] ?? ''}
                onChange={(e) => definir('credencialSiscred', e.target.value)}
              />
              <ListaDeCnaes
                cnaes={dados.cnaes}
                principalGravado={dados.industryClassification}
                consultando={receita.isPending}
              />
            </div>
          </Cartao>
        </fieldset>

        {editar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={editar.error} />}
        {editar.isSuccess && !mudou && (
          <Aviso tom="positivo" titulo="Cadastro salvo">
            As alterações valem a partir de agora. Contratos já gerados guardam os dados que tinham
            na geração.
          </Aviso>
        )}

        {!leitura && (
          <div className="flex flex-wrap items-center gap-3">
            <Botao type="submit" disabled={!mudou} carregando={editar.isPending}>
              Salvar alterações
            </Botao>
            {mudou && (
              <span className="text-xs text-conteudo-sutil">
                {Object.keys(alteracoes()).length} campo(s) alterado(s) — só eles são enviados.
              </span>
            )}
          </div>
        )}
      </form>

      {props.ocultarDesativacao !== true && (
        <DesativacaoDaEmpresa
          businessId={props.businessId}
          {...(props.rotaAposDesativar === undefined
            ? {}
            : { rotaAposDesativar: props.rotaAposDesativar })}
        />
      )}
    </div>
  );
}

/* ─── CNAEs ───────────────────────────────────────────────────────────────── */

/**
 * Os CNAEs como a Receita informa — só leitura: quem muda CNAE é a Receita.
 * Antes da primeira consulta, o principal que veio no cadastro (se veio).
 */
function ListaDeCnaes(props: {
  readonly cnaes: readonly CnaeDaEmpresa[];
  readonly principalGravado: string | null;
  readonly consultando: boolean;
}): JSX.Element {
  const ordenados = [...props.cnaes].sort((a, b) => Number(b.principal) - Number(a.principal));

  return (
    <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
      <span className="text-sm font-medium text-conteudo-secundario">
        CNAEs{ordenados.length > 0 ? ` (${ordenados.length})` : ''}
      </span>
      {ordenados.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">
          {props.consultando
            ? 'Consultando a Receita…'
            : (props.principalGravado ?? 'A Receita não informou CNAEs para este CNPJ.')}
        </p>
      ) : (
        <ul className="divide-y divide-borda rounded-md border border-borda">
          {ordenados.map((c) => (
            <li key={c.codigo} className="flex items-baseline gap-3 px-3 py-2 text-sm">
              <span className="valor-fiscal shrink-0 text-conteudo-primario">{c.codigo}</span>
              <span className="min-w-0 flex-1 text-conteudo-secundario">{c.descricao}</span>
              {c.principal && (
                <Selo tom="informativo" tamanho="pequeno">
                  Principal
                </Selo>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── Desativar ───────────────────────────────────────────────────────────── */

/**
 * Desativar fica separado do formulário, e no fim da página: é a única ação
 * do cadastro que não se desfaz clicando "salvar" de novo. Componente próprio
 * para a aba de cadastro poder colocá-lo depois das outras seções.
 */
export function DesativacaoDaEmpresa(props: {
  readonly businessId: string;
  readonly rotaAposDesativar?: string;
}): JSX.Element | null {
  const navegar = useNavigate();
  const desativar = useDesativarEmpresa();
  const { confirmar } = useDialogo();
  // A mesma chave do formulário: quando ele já carregou, isto é leitura do cache.
  const empresa = useQuery({
    queryKey: ['empresa', props.businessId],
    queryFn: () => requisicaoAutenticada<EmpresaDetalhada>(`/business/${props.businessId}`),
  });

  const dados = empresa.data;
  if (dados === undefined) return null;

  return (
    <Cartao como="section" titulo="Desativar empresa" variante="contorno">
      <p className="text-sm text-conteudo-secundario">
        A empresa some das listagens e dos vínculos. O histórico — contratos, cálculos, documentos —
        continua no banco; nada é apagado.
      </p>
      <div className="mt-3">
        <Botao
          variante="perigo"
          carregando={desativar.isPending}
          onClick={async () => {
            const ok = await confirmar({
              titulo: `Desativar ${dados.legalName}?`,
              descricao:
                'Ela sai das listagens e dos vínculos. O histórico — contratos, cálculos, documentos — permanece no banco.',
              rotuloConfirmar: 'Desativar',
              tom: 'perigo',
            });
            if (!ok) return;
            desativar.mutate(props.businessId, {
              onSuccess: () => navegar(props.rotaAposDesativar ?? '/empresas', { replace: true }),
            });
          }}
        >
          Desativar
        </Botao>
      </div>
      {desativar.isError && (
        <div className="mt-3">
          <ErroDaApi titulo="Não foi possível desativar" erro={desativar.error} />
        </div>
      )}
    </Cartao>
  );
}
